import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Crown, ExternalLink, Phone, Mail, MapPin, ArrowRight, Sparkles } from "lucide-react";

export default function Spotlight() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/spotlights").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const featured = items[0];
  const rest = items.slice(1);

  return (
    <div className="grain min-h-screen" data-testid="spotlight-page">
      {/* Editorial masthead */}
      <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-16">
        <div className="label-eyebrow" style={{ color: "#D4AF37" }}>The Editorial</div>
        <h1 className="mt-4 font-serif text-5xl sm:text-7xl text-bdu-cream tracking-tight leading-[0.95]">
          Faces of <span className="italic text-bdu-gold">Black Excellence.</span>
        </h1>
        <p className="mt-6 text-bdu-cream/70 max-w-2xl text-lg leading-relaxed">
          A recurring editorial honoring the Black business owners, storytellers, and community builders
          whose work makes The Movement possible. They don't wait for permission — they build the door.
        </p>
      </div>

      {loading ? (
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-24 pb-24 text-center text-bdu-cream/50">Loading…</div>
      ) : !featured ? (
        <div className="max-w-6xl mx-auto px-6 sm:px-10 pt-24 pb-24 text-center text-bdu-cream/50" data-testid="spotlight-empty">
          No honorees yet.
        </div>
      ) : (
        <>
          {/* Featured — magazine-style hero */}
          <section className="max-w-6xl mx-auto px-6 sm:px-10 mt-16 pb-24" data-testid="spotlight-featured">
            <div className="grid lg:grid-cols-5 gap-10 items-start">
              {/* Portrait */}
              <div className="lg:col-span-2 relative">
                <div className="absolute -top-3 -left-3 border border-bdu-gold/40 w-20 h-20 pointer-events-none" />
                <div className="absolute -bottom-3 -right-3 border border-bdu-gold/40 w-20 h-20 pointer-events-none" />
                <div className="relative border border-bdu-gold/25 bg-bdu-black overflow-hidden">
                  {featured.hero_image_url ? (
                    <img src={featured.hero_image_url} alt={featured.name} className="w-full h-auto block" data-testid="spotlight-hero-img" />
                  ) : (
                    <div className="aspect-[4/5] flex items-center justify-center text-bdu-cream/25 font-serif text-6xl">{featured.name?.[0] || "?"}</div>
                  )}
                </div>
              </div>

              {/* Editorial */}
              <div className="lg:col-span-3">
                <div className="inline-flex items-center gap-2 border border-bdu-gold/50 bg-bdu-gold/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-bdu-gold">
                  <Crown size={12} /> Current Honoree
                </div>
                <h2 className="mt-6 font-serif text-5xl sm:text-6xl text-bdu-cream leading-[0.95] tracking-tight" data-testid="spotlight-name">
                  {featured.name}
                </h2>
                {featured.title && (
                  <div className="mt-4 text-sm uppercase tracking-[0.3em] text-bdu-gold">{featured.title}</div>
                )}
                {featured.business_name && (
                  <div className="mt-2 font-serif text-2xl text-bdu-cream/85 italic">{featured.business_name}</div>
                )}

                {/* Quote */}
                {featured.quote && (
                  <blockquote className="mt-10 relative pl-6" data-testid="spotlight-quote">
                    <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-bdu-gold via-bdu-gold/50 to-transparent" />
                    <p className="font-serif text-2xl sm:text-3xl text-bdu-cream leading-snug italic">
                      &ldquo;{featured.quote}&rdquo;
                    </p>
                  </blockquote>
                )}

                {/* Bio */}
                {featured.bio && (
                  <p className="mt-8 text-bdu-cream/70 text-base leading-relaxed" data-testid="spotlight-bio">
                    {featured.bio}
                  </p>
                )}

                {/* Contact rail */}
                <div className="mt-10 grid sm:grid-cols-2 gap-3">
                  {featured.website && (
                    <a href={featured.website.startsWith("http") ? featured.website : `https://${featured.website}`}
                       target="_blank" rel="noreferrer"
                       className="flex items-center gap-3 p-4 border border-bdu-gold/25 hover:border-bdu-gold transition-colors group"
                       data-testid="spotlight-website">
                      <ExternalLink size={16} className="text-bdu-gold" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-bdu-cream/50">Website</div>
                        <div className="text-sm text-bdu-cream truncate group-hover:text-bdu-gold">{featured.website.replace(/^https?:\/\//, "")}</div>
                      </div>
                    </a>
                  )}
                  {featured.phone && (
                    <a href={`tel:${featured.phone.replace(/[^0-9+]/g, "")}`}
                       className="flex items-center gap-3 p-4 border border-bdu-gold/25 hover:border-bdu-gold transition-colors group"
                       data-testid="spotlight-phone">
                      <Phone size={16} className="text-bdu-gold" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-bdu-cream/50">Direct</div>
                        <div className="text-sm text-bdu-cream group-hover:text-bdu-gold">{featured.phone}</div>
                      </div>
                    </a>
                  )}
                  {featured.email && (
                    <a href={`mailto:${featured.email}`}
                       className="flex items-center gap-3 p-4 border border-bdu-gold/25 hover:border-bdu-gold transition-colors group"
                       data-testid="spotlight-email">
                      <Mail size={16} className="text-bdu-gold" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-bdu-cream/50">Email</div>
                        <div className="text-sm text-bdu-cream truncate group-hover:text-bdu-gold">{featured.email}</div>
                      </div>
                    </a>
                  )}
                  {featured.location && (
                    <div className="flex items-center gap-3 p-4 border border-bdu-gold/25">
                      <MapPin size={16} className="text-bdu-gold" />
                      <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest text-bdu-cream/50">Based In</div>
                        <div className="text-sm text-bdu-cream">{featured.location}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Gallery */}
            {featured.gallery_urls?.length > 1 && (
              <div className="mt-24" data-testid="spotlight-gallery">
                <div className="label-eyebrow">Selected Work</div>
                <h3 className="mt-2 font-serif text-3xl text-bdu-cream">The Portfolio.</h3>
                <div className="mt-8 grid sm:grid-cols-2 gap-6">
                  {featured.gallery_urls.map((url, i) => (
                    <div key={i} className="border border-bdu-gold/15 bg-bdu-black">
                      <img src={url} alt={`${featured.name} — work ${i + 1}`} className="w-full h-auto block" />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* Past honorees */}
          {rest.length > 0 && (
            <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24 border-t border-bdu-gold/10 pt-16" data-testid="spotlight-past">
              <div className="label-eyebrow">Previously Honored</div>
              <h3 className="mt-2 font-serif text-3xl text-bdu-cream">The Wall.</h3>
              <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {rest.map((p) => (
                  <article key={p.id} className="border border-bdu-gold/15 bg-bdu-surface hover:border-bdu-gold/40 transition-colors" data-testid={`spotlight-past-${p.id}`}>
                    {p.hero_image_url && (
                      <img src={p.hero_image_url} alt={p.name} className="w-full h-56 object-cover block" />
                    )}
                    <div className="p-5">
                      <div className="label-eyebrow" style={{ fontSize: "0.6rem" }}>{p.title || "Honoree"}</div>
                      <h4 className="mt-2 font-serif text-2xl text-bdu-cream leading-tight">{p.name}</h4>
                      {p.business_name && <div className="mt-1 text-xs text-bdu-cream/50">{p.business_name}</div>}
                    </div>
                  </article>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 pb-24">
        <div className="border border-bdu-gold/25 bg-bdu-surface p-10 text-center">
          <div className="label-eyebrow">Know Someone Who Belongs Here?</div>
          <h3 className="mt-4 font-serif text-3xl text-bdu-cream">Nominate the next Face of Black Excellence.</h3>
          <p className="mt-4 text-bdu-cream/60 max-w-xl mx-auto">
            Whether they publish, build, heal, or organize — we want to see them. Email us and we'll consider them for the next spotlight.
          </p>
          <a href="mailto:greg@blackdollarsunited.org?subject=Nominate a Face of Black Excellence" className="btn-gold mt-8 inline-flex" data-testid="spotlight-nominate">
            <Sparkles size={16} /> Nominate an Honoree
          </a>
        </div>
      </section>
    </div>
  );
}


/** Compact landing-page teaser card. Exported so Landing.jsx can drop it in. */
export function SpotlightTeaser() {
  const [item, setItem] = useState(null);
  useEffect(() => {
    api.get("/spotlights/featured").then((r) => setItem(r.data)).catch(() => {});
  }, []);
  if (!item) return null;

  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-24" data-testid="spotlight-teaser">
      <div className="label-eyebrow">Faces of Black Excellence</div>
      <h2 className="mt-3 font-serif text-4xl sm:text-5xl text-bdu-cream tracking-tight">
        The people <span className="italic text-bdu-gold">already</span> building it.
      </h2>

      <div className="mt-12 grid lg:grid-cols-5 gap-8 items-center">
        <div className="lg:col-span-2 relative">
          <div className="absolute -top-2 -left-2 border border-bdu-gold/40 w-16 h-16 pointer-events-none" />
          <div className="border border-bdu-gold/25 bg-bdu-black overflow-hidden">
            {item.hero_image_url ? (
              <img src={item.hero_image_url} alt={item.name} className="w-full h-auto block" data-testid="spotlight-teaser-img" />
            ) : (
              <div className="aspect-[4/5] flex items-center justify-center text-bdu-cream/25 font-serif text-6xl">{item.name?.[0]}</div>
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="inline-flex items-center gap-2 border border-bdu-gold/50 bg-bdu-gold/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] text-bdu-gold">
            <Crown size={12} /> Current Honoree
          </div>
          <h3 className="mt-5 font-serif text-4xl text-bdu-cream leading-tight" data-testid="spotlight-teaser-name">{item.name}</h3>
          {item.title && <div className="mt-2 text-xs uppercase tracking-[0.3em] text-bdu-gold">{item.title}</div>}
          {item.business_name && <div className="mt-1 text-lg italic text-bdu-cream/70">{item.business_name}</div>}
          {item.quote && (
            <blockquote className="mt-6 pl-5 border-l-2 border-bdu-gold/60 font-serif text-xl text-bdu-cream/90 leading-snug italic">
              &ldquo;{item.quote.slice(0, 180)}{item.quote.length > 180 ? "…" : ""}&rdquo;
            </blockquote>
          )}
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/spotlight" className="btn-gold" data-testid="spotlight-teaser-cta">
              Read The Feature <ArrowRight size={16} />
            </Link>
            {item.website && (
              <a href={item.website.startsWith("http") ? item.website : `https://${item.website}`} target="_blank" rel="noreferrer" className="btn-ghost">
                <ExternalLink size={14} /> Visit {item.business_name?.split(" ")[0] || "Site"}
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
